import {
  CircleX,
  Loader,
  Phone,
  PhoneCall,
  ShoppingBag,
  Truck,
} from "lucide-react-native";
import {
  hasBeenRefunded,
  isCallCompleted,
  isExpired,
  isPrepaid,
  isVoided,
} from "../utils/call-helpers";
import { ICall } from "../types/call";
import { useViewport } from "../hooks/useViewport";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./common/shadcn/tooltip";
import StyledText from "./common/ui/styled-text";
import { Badge } from "./badge";
import { View } from "react-native";

type OrderCallStatus = "Expired" | "Voided" | "Refunded" | "Prepaid";
const getOrderCallStatus = (call: ICall): OrderCallStatus | null => {
  return isExpired(call)
    ? "Expired"
    : isVoided(call)
      ? "Voided"
      : hasBeenRefunded(call)
        ? "Refunded"
        : isPrepaid(call)
          ? "Prepaid"
          : null;
};

export const OrderCallIconWithStatus = ({ call }: { call: ICall }) => {
  const { isDesktop } = useViewport();

  const status = getOrderCallStatus(call);
  return (
    <View
      className={`flex flex-col items-center justify-center ${isDesktop ? "gap-1" : "gap-0.5"}`}
    >
      <View className="flex items-center justify-center [transform-origin:center]">
        <OrderCallIcon call={call} />
      </View>
      {status && <OrderCallStatusBadge status={status} />}
    </View>
  );
};

export const OrderCallIcon = ({ call }: { call: ICall }) => {
  if (isVoided(call)) {
    return <CircleX size={18} className="text-red-600" />;
  }

  const isDeliveryCall = call.order?.deliveryAddress != null;
  const OrderIcon = isDeliveryCall ? Truck : ShoppingBag;
  const getStatusColor = () => {
    if (isExpired(call)) {
      return "text-red-600";
    } else if (hasBeenRefunded(call)) {
      return "text-orange-400";
    } else {
      return "text-green-500";
    }
  };

  return <OrderIcon size={18} className={getStatusColor()} />;
};

const OrderCallStatusBadge = ({ status }: { status: OrderCallStatus }) => {
  const { isDesktop } = useViewport();
  const statusBadge = {
    Expired: "bg-gray-50/20 border-gray-200 border-dotted",
    Voided: "bg-gray-50 border-gray-200",
    Refunded: "bg-orange-50 border-orange-200",
    Prepaid: "bg-green-50 border-green-200",
  };
  const statusTextColor = {
    Expired: "text-gray-600",
    Voided: "text-gray-700",
    Refunded: "text-orange-700",
    Prepaid: "text-green-700",
  };
  const tooltipContent = {
    Expired:
      "Since the payment was not completed, the order has been cancelled.",
    Voided: "The customer has voided this order.",
    Refunded: "The customer has been refunded for this order.",
    Prepaid: "The customer has already paid for this order.",
  };
  if (isDesktop) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`gap-1 cursor-pointer ${statusBadge[status]}`}
          >
            <StyledText
              className={`font-medium text-xs ${statusTextColor[status]}`}
            >
              {status}
            </StyledText>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipContent[status]}</p>
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <StyledText
      className={`font-medium text-[10px] ${statusTextColor[status]}`}
    >
      {status}
    </StyledText>
  );
};

export const PhoneCallIcon = ({ call }: { call: ICall }) => {
  if (isCallCompleted(call)) {
    return <Phone size={18} className="text-gray-500" />;
  } else {
    return <PhoneCall size={18} className="text-gray-500" />;
  }
};

export const ProcessingCallIcon = () => {
  return <Loader size={18} className="text-amber-700" />;
};
